import React, { useEffect, useMemo, useState } from "react";

import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import {
	CheckCircle2,
	Eye,
	ListChecks,
	Loader2,
	Pencil,
	Play,
	Plus,
	ShieldAlert,
	Trash2,
	XCircle,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../components/ui/dialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import { cn } from "../../lib/utils";
import toastError from "../../errors/toastError";
import api from "../../services/api";

// O cenário de teste: as falas que o "cliente" dá e o que a IA precisa ter
// feito para o roteiro ser considerado aprovado. Rodar dá um placar, e o
// detalhe de cada expectativa mostra por que ela passou ou não.

// A lista vem do servidor: o backend é quem sabe o que o cenário aceita.
const buscarCenarios = (agenteId) =>
	api
		.get("/ai/scenarios", { params: { agentId: agenteId } })
		.then(({ data }) => (Array.isArray(data) ? data : []));

const TIPOS_DE_EXPECTATIVA = [
	{ value: "calls_tool", rotulo: "Chama a ferramenta", pedeValor: true },
	{ value: "not_calls_action", rotulo: "Não executa ação", pedeValor: false },
	{ value: "handoff", rotulo: "Passa para humano", pedeValor: false },
	{ value: "no_handoff", rotulo: "Não passa para humano", pedeValor: false },
	{ value: "reply_contains", rotulo: "A resposta contém", pedeValor: true },
	{
		value: "reply_not_contains",
		rotulo: "Nenhuma resposta contém",
		pedeValor: true,
	},
];

const MAX_FALAS = 20;

const rotuloDaExpectativa = (tipo) =>
	TIPOS_DE_EXPECTATIVA.find((t) => t.value === tipo)?.rotulo || tipo;

const FORMULARIO_VAZIO = {
	id: null,
	name: "",
	turns: "",
	expectations: [],
};

const dataDaRodada = (valor) => {
	if (!valor) return "";

	try {
		return format(parseISO(valor), "dd/MM/yyyy HH:mm");
	} catch {
		return "";
	}
};

const AbaCenarios = ({ agentes = [] }) => {
	const [agenteId, setAgenteId] = useState("");
	const [cenarios, setCenarios] = useState([]);
	const [carregando, setCarregando] = useState(true);
	// O cenário em execução (ou "todos"): enquanto roda, a lista espera.
	const [rodando, setRodando] = useState(null);
	const [placar, setPlacar] = useState(null);
	const [modal, setModal] = useState(false);
	const [form, setForm] = useState(FORMULARIO_VAZIO);
	const [erros, setErros] = useState({});
	const [salvando, setSalvando] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [selecionado, setSelecionado] = useState(null);
	const [execucao, setExecucao] = useState(null);
	const [avisoModulo, setAvisoModulo] = useState(null);

	// Sem agente escolhido, o primeiro da lista já vem na mão.
	const agenteEscolhido =
		agenteId || (agentes.length ? String(agentes[0].id) : "");

	const agente = agentes.find(
		(item) => String(item.id) === String(agenteEscolhido)
	);

	// A ferramenta em rascunho também entra na lista: o backend testa o
	// agente com elas, e é justamente a ferramenta que o assistente acabou
	// de criar que o cenário precisa conseguir escolher.
	const ferramentasDoAgente = useMemo(
		() =>
			(agente?.tools || []).filter(
				(ferramenta) =>
					ferramenta.status === "active" || ferramenta.status === "draft"
			),
		[agente]
	);

	const tratarErro = (err) => {
		const status = err?.response?.status;
		const mensagem =
			err?.response?.data?.error || err?.response?.data?.message || "";

		// Empresa sem o módulo de agentes de IA não chega a ver cenário:
		// a tela avisa em vez de tomar erro seco.
		if (status === 403 && mensagem.includes("ERR_MODULE_NOT_CONTRACTED")) {
			setAvisoModulo("Módulo Agentes de IA não contratado");
			return;
		}

		toastError(err);
	};

	const recarregar = async () => {
		if (!agenteEscolhido) return;

		try {
			setCenarios(await buscarCenarios(agenteEscolhido));
		} catch (err) {
			setCenarios([]);
			tratarErro(err);
		}
	};

	useEffect(() => {
		if (!agenteEscolhido) return;

		let cancelado = false;

		buscarCenarios(agenteEscolhido)
			.then((lista) => {
				if (cancelado) return;
				setCenarios(lista);
				setCarregando(false);
			})
			.catch((err) => {
				if (cancelado) return;
				setCenarios([]);
				setCarregando(false);
				tratarErro(err);
			});

		return () => {
			cancelado = true;
		};
	}, [agenteEscolhido]);

	const abrirNovo = () => {
		setForm(FORMULARIO_VAZIO);
		setErros({});
		setModal(true);
	};

	const abrirEdicao = (cenario) => {
		setForm({
			id: cenario.id,
			name: cenario.name || "",
			// As falas voltam como texto: uma por linha é como o admin
			// escreve o roteiro.
			turns: (cenario.turns || []).join("\n"),
			expectations: (cenario.expectations || []).map((expectativa) => ({
				type: expectativa.type,
				value: expectativa.value || "",
			})),
		});
		setErros({});
		setModal(true);
	};

	const atualizarExpectativa = (indice, mudanca) => {
		setForm((atual) => ({
			...atual,
			expectations: atual.expectations.map((expectativa, posicao) =>
				posicao === indice ? { ...expectativa, ...mudanca } : expectativa
			),
		}));
	};

	const removerExpectativa = (indice) => {
		setForm((atual) => ({
			...atual,
			expectations: atual.expectations.filter(
				(_, posicao) => posicao !== indice
			),
		}));
	};

	const salvar = async () => {
		const novosErros = {};

		const falas = form.turns
			.split("\n")
			.map((fala) => fala.trim())
			.filter(Boolean);

		// As mesmas regras do servidor: avisar aqui evita o erro seco.
		if (form.name.trim().length < 2) {
			novosErros.name = "Dê um nome para o cenário.";
		}

		if (!falas.length) {
			novosErros.turns = "Escreva pelo menos uma fala.";
		} else if (falas.length > MAX_FALAS) {
			novosErros.turns = `Um cenário tem no máximo ${MAX_FALAS} falas.`;
		}

		const expectativas = form.expectations.map((expectativa) => ({
			type: expectativa.type,
			value: (expectativa.value || "").trim(),
		}));

		if (
			expectativas.some(
				(expectativa) =>
					expectativa.value === "" &&
					["calls_tool", "reply_contains", "reply_not_contains"].includes(
						expectativa.type
					)
			)
		) {
			novosErros.expectations =
				"Expectativa sem valor não faz sentido: escolha o que era esperado.";
		}

		if (Object.keys(novosErros).length) {
			setErros(novosErros);
			return;
		}

		setErros({});
		setSalvando(true);

		const dados = {
			name: form.name.trim(),
			agentId: Number(agenteEscolhido),
			turns: falas,
			expectations: expectativas.map((expectativa) => ({
				type: expectativa.type,
				...(expectativa.value ? { value: expectativa.value } : {}),
			})),
		};

		try {
			if (form.id) {
				await api.put(`/ai/scenarios/${form.id}`, dados);
			} else {
				await api.post("/ai/scenarios", dados);
			}

			toast.success(form.id ? "Cenário atualizado." : "Cenário criado.");
			setModal(false);
			await recarregar();
		} catch (err) {
			tratarErro(err);
		}

		setSalvando(false);
	};

	const excluir = async () => {
		try {
			await api.delete(`/ai/scenarios/${selecionado.id}`);
			toast.success("Cenário removido.");
			await recarregar();
		} catch (err) {
			tratarErro(err);
		}

		setSelecionado(null);
	};

	const rodar = async (cenario) => {
		setRodando(cenario.id);

		try {
			const { data } = await api.post(`/ai/scenarios/${cenario.id}/run`);
			const passou = Boolean(data?.lastResult?.passou);

			toast.success(
				passou
					? "Cenário passou."
					: "Cenário falhou: veja o que era esperado."
			);

			await recarregar();
		} catch (err) {
			tratarErro(err);
		}

		setRodando(null);
	};

	const rodarTodos = async () => {
		setRodando("todos");

		try {
			const { data } = await api.post("/ai/scenarios/run-all", {
				agentId: Number(agenteEscolhido),
			});

			setPlacar(data);
			toast.success(`${data?.passaram || 0} de ${data?.total || 0} passaram.`);

			await recarregar();
		} catch (err) {
			tratarErro(err);
		}

		setRodando(null);
	};

	const ultimoResultado = (cenario) => cenario?.lastResult || null;

	const resultadoDoCenario = (cenario) => {
		const resultado = ultimoResultado(cenario);

		if (!resultado) return { passou: false, nuncaRodou: true };

		return { passou: Boolean(resultado.passou), nuncaRodou: false };
	};

	if (avisoModulo) {
		return (
			<div className="rounded-xl border bg-card py-16 text-center">
				<ShieldAlert className="mx-auto h-8 w-8 text-muted-foreground/50" />
				<p className="mt-3 text-sm font-medium">{avisoModulo}</p>
				<p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
					Cenários de teste fazem parte do módulo de agentes de IA. Fale com
					quem administra a sua conta para liberar o módulo.
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			<ConfirmationModal
				title={selecionado && `Remover ${selecionado.name}?`}
				open={Boolean(confirmOpen)}
				onClose={() => {
					setConfirmOpen(false);
					setSelecionado(null);
				}}
				onConfirm={excluir}
				confirmLabel="Remover"
			>
				O roteiro e o último resultado deste cenário serão apagados.
			</ConfirmationModal>

			<div className="flex flex-wrap items-end gap-2">
				<div className="min-w-[240px] flex-1 space-y-1.5">
					<Label>Agente</Label>
					<Select
						value={agenteEscolhido}
						onValueChange={(v) => {
							setAgenteId(v);
							setCarregando(true);
							setPlacar(null);
						}}
						disabled={Boolean(rodando)}
					>
						<SelectTrigger aria-label="Agente dos cenários">
							<SelectValue placeholder="Escolha o agente" />
						</SelectTrigger>
						<SelectContent>
							{agentes.map((item) => (
								<SelectItem key={item.id} value={String(item.id)}>
									{item.status === "draft"
										? `${item.name} (rascunho)`
										: item.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<p className="text-xs text-muted-foreground">
						{agentes.length === 0
							? "Crie um agente na aba Agentes para testar aqui."
							: "Cada cenário roda numa conversa nova, sem tocar em atendimento real."}
					</p>
				</div>

				<Button
					variant="outline"
					className="h-9"
					disabled={Boolean(rodando) || !agenteEscolhido || !cenarios.length}
					onClick={rodarTodos}
				>
					{rodando === "todos" ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<Play className="h-4 w-4" />
					)}
					Rodar todos
				</Button>

				<Button
					className="h-9"
					disabled={Boolean(rodando) || !agenteEscolhido}
					onClick={abrirNovo}
				>
					<Plus className="h-4 w-4" />
					<span className="hidden sm:inline">Novo cenário</span>
					<span className="sm:hidden">Novo</span>
				</Button>
			</div>

			{/* ---------------- placar do "rodar todos" ---------------- */}
			{placar && (
				<section className="rounded-xl border bg-card p-4">
					<div className="flex flex-wrap items-center gap-2">
						<p className="text-sm font-medium">
							{placar.passaram} de {placar.total} passaram
						</p>
						<Button
							variant="ghost"
							size="sm"
							className="h-7 text-xs"
							onClick={() => setPlacar(null)}
						>
							Fechar
						</Button>
					</div>

					{placar.cenarios?.length > 0 && (
						<ul className="mt-2 space-y-1">
							{placar.cenarios.map((item) => (
								<li key={item.id} className="flex items-center gap-2 text-xs">
									{item.passou ? (
										<CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
									) : (
										<XCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
									)}
									<span className="truncate">{item.name}</span>
								</li>
							))}
						</ul>
					)}
				</section>
			)}

			{/* ---------------- lista ---------------- */}
			{!agenteEscolhido ? (
				<div className="rounded-xl border bg-card py-16 text-center">
					<ListChecks className="mx-auto h-8 w-8 text-muted-foreground/50" />
					<p className="mt-3 text-sm font-medium">Nenhum agente escolhido</p>
					<p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
						Crie um agente na aba Agentes para escrever cenários de teste
						dele.
					</p>
				</div>
			) : carregando && agenteEscolhido ? (
				<div className="flex items-center justify-center rounded-xl border bg-card py-16">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			) : cenarios.length === 0 ? (
				<div className="rounded-xl border bg-card py-16 text-center">
					<ListChecks className="mx-auto h-8 w-8 text-muted-foreground/50" />
					<p className="mt-3 text-sm font-medium">Nenhum cenário criado</p>
					<p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
						Um cenário é o roteiro de uma conversa e o que a IA precisa fazer
						nela. Escreva um e rode para ver o placar.
					</p>
				</div>
			) : (
				<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
					{cenarios.map((cenario) => {
						const resultado = resultadoDoCenario(cenario);
						const ultimo = ultimoResultado(cenario);
						const rodandoEste = rodando === cenario.id;

						return (
							<div
								key={cenario.id}
								className="flex flex-col rounded-xl border bg-card p-4"
							>
								<div className="flex items-start justify-between gap-2">
									<div className="min-w-0">
										<p className="truncate font-medium">{cenario.name}</p>
										<p className="text-xs text-muted-foreground">
											{cenario.turns?.length || 0} fala(s) ·{" "}
											{cenario.expectations?.length || 0} expectativa(s)
										</p>
									</div>

									{resultado.nuncaRodou ? (
										<span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
											Nunca rodou
										</span>
									) : resultado.passou ? (
										<span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
											<CheckCircle2 className="h-3 w-3" />
											Passou
										</span>
									) : (
										<span className="flex shrink-0 items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-medium text-destructive">
											<XCircle className="h-3 w-3" />
											Falhou
										</span>
									)}
								</div>

								<p className="mt-2 text-xs text-muted-foreground">
									{cenario.lastRunAt
										? `Última execução em ${dataDaRodada(cenario.lastRunAt)}`
										: "Este cenário ainda não rodou."}
									{ultimo && typeof ultimo.custo === "number" && ultimo.custo > 0
										? ` · US$ ${ultimo.custo.toFixed(4)}`
										: ""}
								</p>

								<div className="mt-3 flex flex-wrap gap-1 border-t pt-3">
									<Button
										variant="outline"
										size="sm"
										className="h-7 text-xs"
										disabled={Boolean(rodando)}
										onClick={() => rodar(cenario)}
									>
										{rodandoEste ? (
											<Loader2 className="h-3 w-3 animate-spin" />
										) : (
											<Play className="h-3 w-3" />
										)}
										Rodar
									</Button>

									<Button
										variant="ghost"
										size="sm"
										className="h-7 text-xs"
										disabled={Boolean(rodando)}
										onClick={() => setExecucao(cenario)}
									>
										<Eye className="h-3 w-3" />
										Ver última execução
									</Button>

									<Button
										variant="ghost"
										size="icon"
										className="h-7 w-7"
										aria-label={`Editar ${cenario.name}`}
										disabled={Boolean(rodando)}
										onClick={() => abrirEdicao(cenario)}
									>
										<Pencil className="h-3.5 w-3.5" />
									</Button>

									<Button
										variant="ghost"
										size="icon"
										className="h-7 w-7 text-destructive"
										aria-label={`Remover ${cenario.name}`}
										disabled={Boolean(rodando)}
										onClick={() => {
											setSelecionado(cenario);
											setConfirmOpen(true);
										}}
									>
										<Trash2 className="h-3.5 w-3.5" />
									</Button>
								</div>
							</div>
						);
					})}
				</div>
			)}

			{/* ---------------- diálogo de edição ---------------- */}
			<Dialog open={modal} onOpenChange={() => setModal(false)}>
				<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
					<DialogHeader>
						<DialogTitle>
							{form.id ? "Editar cenário" : "Novo cenário"}
						</DialogTitle>
						<DialogDescription>
							As falas são o que o cliente diz; as expectativas são o que a
							IA precisa ter feito para o roteiro passar.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-4">
						<div className="space-y-1.5">
							<Label htmlFor="cenario-nome">Nome</Label>
							<Input
								id="cenario-nome"
								value={form.name}
								onChange={(e) =>
									setForm({ ...form, name: e.target.value })
								}
								placeholder="Cliente pedindo segunda via do boleto"
							/>
							{erros.name && (
								<p className="text-xs text-destructive">{erros.name}</p>
							)}
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="cenario-falas">Falas do cliente</Label>
							<Textarea
								id="cenario-falas"
								rows={6}
								value={form.turns}
								onChange={(e) =>
									setForm({ ...form, turns: e.target.value })
								}
								placeholder={"Oi, preciso da segunda via do meu boleto\nMeu CPF é 123.456.789-00"}
							/>
							<p className="text-xs text-muted-foreground">
								Uma fala por linha, até {MAX_FALAS}. A primeira linha é a
								primeira mensagem enviada ao agente.
							</p>
							{erros.turns && (
								<p className="text-xs text-destructive">{erros.turns}</p>
							)}
						</div>

						<div className="space-y-2">
							<div className="flex items-center justify-between gap-2">
								<Label>O que era esperado</Label>
								<Button
									type="button"
									variant="outline"
									size="sm"
									className="h-7 text-xs"
									onClick={() =>
										setForm({
											...form,
											expectations: [
												...form.expectations,
												{ type: "calls_tool", value: "" },
											],
										})
									}
								>
									<Plus className="h-3 w-3" />
									Expectativa
								</Button>
							</div>

							{form.expectations.length === 0 && (
								<p className="text-xs text-muted-foreground">
									Sem expectativa o cenário só avisa se a conversa rodou — sem
									placar de certo ou errado.
								</p>
							)}

							{form.expectations.map((expectativa, indice) => {
								const tipo = TIPOS_DE_EXPECTATIVA.find(
									(item) => item.value === expectativa.type
								);

								return (
									<div
										key={indice}
										className="flex flex-wrap items-center gap-2 rounded-lg border p-2"
									>
										<Select
											value={expectativa.type}
											onValueChange={(v) =>
												atualizarExpectativa(indice, {
													type: v,
													value: "",
												})
											}
										>
											<SelectTrigger
												className="h-8 w-[220px]"
												aria-label="Tipo da expectativa"
											>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{TIPOS_DE_EXPECTATIVA.map((item) => (
													<SelectItem key={item.value} value={item.value}>
														{item.rotulo}
													</SelectItem>
												))}
											</SelectContent>
										</Select>

										{expectativa.type === "calls_tool" ? (
											<Select
												value={expectativa.value || "nenhuma"}
												onValueChange={(v) =>
													atualizarExpectativa(indice, {
														value: v === "nenhuma" ? "" : v,
													})
												}
											>
												<SelectTrigger
													className="h-8 w-[200px]"
													aria-label="Ferramenta esperada"
												>
													<SelectValue placeholder="Escolha a ferramenta" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="nenhuma">Escolher</SelectItem>
													{ferramentasDoAgente.map((ferramenta) => (
														<SelectItem
															key={ferramenta.id}
															value={ferramenta.name}
														>
															{ferramenta.status === "draft"
																? `${ferramenta.name} (rascunho)`
																: ferramenta.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										) : tipo?.pedeValor ? (
											<Input
												className="h-8 flex-1"
												value={expectativa.value}
												onChange={(e) =>
													atualizarExpectativa(indice, {
														value: e.target.value,
													})
												}
												placeholder="Texto esperado na resposta"
												aria-label="Valor da expectativa"
											/>
										) : (
											<span className="flex-1 text-xs text-muted-foreground">
												Sem valor: esta expectativa só olha se aconteceu.
											</span>
										)}

										<Button
											type="button"
											variant="ghost"
											size="icon"
											className="h-8 w-8 text-destructive"
											aria-label="Remover expectativa"
											onClick={() => removerExpectativa(indice)}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</div>
								);
							})}

							{erros.expectations && (
								<p className="text-xs text-destructive">{erros.expectations}</p>
							)}
						</div>
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={() => setModal(false)}>
							Cancelar
						</Button>
						<Button onClick={salvar} disabled={salvando}>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							{form.id ? "Salvar" : "Criar cenário"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* ---------------- diálogo da última execução ---------------- */}
			<Dialog open={Boolean(execucao)} onOpenChange={() => setExecucao(null)}>
				<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
					<DialogHeader>
						<DialogTitle>{execucao?.name}</DialogTitle>
						<DialogDescription>
							Como a última execução deste cenário terminou.
						</DialogDescription>
					</DialogHeader>

					{!execucao?.lastResult ? (
						<p className="text-xs text-muted-foreground">
							Este cenário ainda não rodou.
						</p>
					) : (
						<div className="space-y-3">
							<div className="flex flex-wrap items-center gap-2">
								{execucao.lastResult.passou ? (
									<>
										<CheckCircle2 className="h-4 w-4 text-emerald-500" />
										<p className="text-sm font-medium">Passou</p>
									</>
								) : (
									<>
										<XCircle className="h-4 w-4 text-destructive" />
										<p className="text-sm font-medium">Falhou</p>
									</>
								)}

								{execucao.lastRunAt && (
									<span className="text-xs text-muted-foreground">
										em {dataDaRodada(execucao.lastRunAt)}
									</span>
								)}

								{typeof execucao.lastResult.custo === "number" &&
									execucao.lastResult.custo > 0 && (
										<span className="text-xs text-muted-foreground">
											· US$ {execucao.lastResult.custo.toFixed(4)}
										</span>
									)}
							</div>

							{/* As expectativas: o porquê de cada uma ter passado ou
							    falhado. */}
							<section className="rounded-lg border p-3">
								<p className="text-sm font-medium">O que era esperado</p>

								{!execucao.lastResult.itens?.length ? (
									<p className="mt-1 text-xs text-muted-foreground">
										Este cenário não tem expectativas: ele só mostra a conversa.
									</p>
								) : (
									<ul className="mt-2 space-y-1.5">
										{execucao.lastResult.itens.map((item, indice) => (
											<li key={indice} className="flex items-start gap-2 text-xs">
												{item.ok ? (
													<CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
												) : (
													<XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
												)}
												<span className="leading-relaxed">
													<span className="font-medium">
														{rotuloDaExpectativa(item.type)}
													</span>
													{item.value ? ` · "${item.value}"` : ""} —{" "}
													{item.detalhe}
												</span>
											</li>
										))}
									</ul>
								)}
							</section>

							{/* A conversa: o que o cliente falou e o que a IA
							    respondeu. */}
							<section className="rounded-lg border p-3">
								<p className="text-sm font-medium">Conversa</p>

								{!execucao.lastResult.conversa?.length ? (
									<p className="mt-1 text-xs text-muted-foreground">
										Nenhuma fala registrada.
									</p>
								) : (
									<div className="mt-2 space-y-2">
										{execucao.lastResult.conversa.map((mensagem, indice) => {
											if (mensagem.role === "sistema") {
												return (
													<p
														key={indice}
														className="text-center text-[11px] leading-relaxed text-muted-foreground"
													>
														{mensagem.texto}
													</p>
												);
											}

											const daCliente = mensagem.role === "cliente";

											return (
												<div
													key={indice}
													className={cn(
														"flex flex-col gap-0.5",
														daCliente ? "items-end" : "items-start"
													)}
												>
													<div
														className={cn(
															"max-w-[85%] whitespace-pre-wrap rounded-xl px-3 py-2 text-xs leading-relaxed",
															daCliente
																? "bg-primary text-primary-foreground"
																: "bg-muted"
														)}
													>
														{mensagem.texto}
													</div>
													{typeof mensagem.ms === "number" && (
														<span className="text-[10px] text-muted-foreground">
															{(mensagem.ms / 1000).toFixed(1)}s
														</span>
													)}
												</div>
											);
										})}
									</div>
								)}
							</section>

							{/* Ação que ficou esperando aprovação: a conversa parou
							    nela, e isso é motivo de falha. */}
							{execucao.lastResult.pendente && (
								<p className="rounded-md bg-amber-500/5 p-2.5 text-xs leading-relaxed text-amber-700 dark:text-amber-400">
									A conversa parou numa ação esperando aprovação:{" "}
									{execucao.lastResult.pendente.description ||
										execucao.lastResult.pendente.toolName}
								</p>
							)}
						</div>
					)}

					<DialogFooter>
						<Button variant="outline" onClick={() => setExecucao(null)}>
							Fechar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default AbaCenarios;
